import { Center, Container, Skeleton, Stack, Tabs } from "@mantine/core";
import { Await } from "@tanstack/react-router";
import type { BeholdPost } from "@/lambda/social/types";
import { useHomeLiveTickerData } from "@webapp/hooks/useHomeLiveTicker";
import type { getHomeNewsFn } from "@webapp/server/functions/news";
import { useState } from "react";
import HomeInstagram from "./HomeInstagram";
import HomeLiveTicker from "./HomeLiveTicker";
import HomeNews from "./HomeNews";
import ScrollAnchor from "./ScrollAnchor";

type HomeContentTab = "news" | "instagram" | "live";
type HomeNewsData = Awaited<ReturnType<typeof getHomeNewsFn>>;

type HomeContentTabsProps = {
  news: Promise<HomeNewsData>;
  instagramPosts: Promise<BeholdPost[]>;
};

const tabClassNames = {
  list: "home-content-tabs-list",
  tab: "home-content-tabs-tab",
} as const;

function TabsFallback() {
  return (
    <Container size="xl" w="100%" py="md" px={{ base: "lg", md: "xl" }}>
      <Stack>
        <Center pb="xs">
          <Tabs value="news" variant="default" color="blumine" classNames={tabClassNames}>
            <Tabs.List>
              <Tabs.Tab value="news">News</Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </Center>
        <Skeleton height={140} maw={620} />
        <Skeleton height={140} maw={620} />
      </Stack>
    </Container>
  );
}

export default function HomeContentTabs({ news, instagramPosts }: HomeContentTabsProps) {
  return (
    <Await promise={news} fallback={<TabsFallback />}>
      {(initialNews) => (
        <Await promise={instagramPosts} fallback={<TabsShell initialNews={initialNews} />}>
          {(posts) => <TabsShell initialNews={initialNews} posts={posts} />}
        </Await>
      )}
    </Await>
  );
}

function TabsShell({ initialNews, posts }: { initialNews: HomeNewsData; posts?: BeholdPost[] }) {
  const { ourMatches, hasMatchesToday, isPending } = useHomeLiveTickerData();
  const showInstagram = (posts?.length ?? 0) > 0;
  const showLive = !isPending && hasMatchesToday;

  const [activeTab, setActiveTab] = useState<HomeContentTab>("news");
  const resolvedTab: HomeContentTab =
    (activeTab === "instagram" && !showInstagram) || (activeTab === "live" && !showLive)
      ? "news"
      : activeTab;

  return (
    <Container size="xl" w="100%" py="md" px={{ base: "lg", md: "xl" }} pos="relative">
      <ScrollAnchor name="news" />
      {showInstagram && <ScrollAnchor name="instagram" />}
      {showLive && <ScrollAnchor name="live" />}
      <Tabs
        value={resolvedTab}
        onChange={(value) => {
          if (value === "news" || value === "instagram" || value === "live") {
            setActiveTab(value);
          }
        }}
        variant="default"
        color="blumine"
        classNames={tabClassNames}
        keepMounted={false}
      >
        <Center pb="xs">
          <Tabs.List>
            <Tabs.Tab value="news">News</Tabs.Tab>
            {showInstagram && <Tabs.Tab value="instagram">Instagram</Tabs.Tab>}
            {showLive && <Tabs.Tab value="live">Live</Tabs.Tab>}
          </Tabs.List>
        </Center>

        <Tabs.Panel value="news" pt="md">
          <HomeNews initialNews={initialNews} hideHeading />
        </Tabs.Panel>

        {showInstagram && posts && (
          <Tabs.Panel value="instagram" pt="md">
            <HomeInstagram posts={posts} hideHeading />
          </Tabs.Panel>
        )}

        {showLive && (
          <Tabs.Panel value="live" pt="md">
            <HomeLiveTicker matches={ourMatches} embedded />
          </Tabs.Panel>
        )}
      </Tabs>
    </Container>
  );
}
